/**
 * `/save-card` feature slice — zero-auth tokenization flow.
 *
 *   createTokenizeSession → BFF `POST /v1/payment-methods/tokenize-session`
 *   savePaymentMethod     → BFF `POST /v1/payment-methods/save` (bearer auth)
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { BffError, paymentMethodsApi } from '../../services';
import type {
  PaymentMethodAuthoriseDetailsRequestBody,
  PaymentMethodAuthoriseRequestBody,
  PaymentMethodAuthoriseResponse,
  PaymentMethodSaveRequestBody,
  PaymentMethodSaveResponse,
  TokenizeSessionResponse,
} from '../../types/api';

export interface TokenizeSliceError {
  code: string;
  message: string;
}

export interface TokenizeState {
  session: TokenizeSessionResponse | null;
  saved: PaymentMethodSaveResponse | null;
  loading: boolean;
  error: TokenizeSliceError | null;
}

const initialState: TokenizeState = {
  session: null,
  saved: null,
  loading: false,
  error: null,
};

function asSliceError(error: unknown): TokenizeSliceError {
  if (error instanceof BffError) return { code: error.code, message: error.message };
  if (error instanceof Error) return { code: 'unknown_error', message: error.message };
  return { code: 'unknown_error', message: 'Unknown error' };
}

export const createTokenizeSession = createAsyncThunk<
  TokenizeSessionResponse,
  { tokenId: string; countryCode: string },
  { rejectValue: TokenizeSliceError }
>('tokenize/createTokenizeSession', async (input, { rejectWithValue }) => {
  try {
    return await paymentMethodsApi.createTokenizeSession(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

export const savePaymentMethod = createAsyncThunk<
  PaymentMethodSaveResponse,
  { tokenizeSessionToken: string; body: PaymentMethodSaveRequestBody },
  { rejectValue: TokenizeSliceError }
>('tokenize/savePaymentMethod', async (input, { rejectWithValue }) => {
  try {
    return await paymentMethodsApi.save(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

/**
 * Advanced-flow zero-auth tokenize. Relays the CSE-encrypted card to the BFF
 * `/authorise` so the server can read the EXACT `recurringDetailReference`.
 */
export const authoriseCard = createAsyncThunk<
  PaymentMethodAuthoriseResponse,
  { tokenizeSessionToken: string; body: PaymentMethodAuthoriseRequestBody },
  { rejectValue: TokenizeSliceError }
>('tokenize/authoriseCard', async (input, { rejectWithValue }) => {
  try {
    return await paymentMethodsApi.authorise(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

/** 3DS2 continuation for a tokenize started by {@link authoriseCard}. */
export const authoriseCardDetails = createAsyncThunk<
  PaymentMethodAuthoriseResponse,
  { tokenizeSessionToken: string; body: PaymentMethodAuthoriseDetailsRequestBody },
  { rejectValue: TokenizeSliceError }
>('tokenize/authoriseCardDetails', async (input, { rejectWithValue }) => {
  try {
    return await paymentMethodsApi.authoriseDetails(input);
  } catch (error) {
    return rejectWithValue(asSliceError(error));
  }
});

const tokenizeSlice = createSlice({
  name: 'tokenize',
  initialState,
  reducers: {
    resetTokenize(): TokenizeState {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTokenizeSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createTokenizeSession.fulfilled,
        (state, action: PayloadAction<TokenizeSessionResponse>) => {
          state.loading = false;
          state.session = action.payload;
        },
      )
      .addCase(createTokenizeSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'createTokenizeSession failed',
        };
      })
      .addCase(savePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        savePaymentMethod.fulfilled,
        (state, action: PayloadAction<PaymentMethodSaveResponse>) => {
          state.loading = false;
          state.saved = action.payload;
        },
      )
      .addCase(savePaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'savePaymentMethod failed',
        };
      })
      .addCase(authoriseCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authoriseCard.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(authoriseCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'authoriseCard failed',
        };
      })
      .addCase(authoriseCardDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authoriseCardDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(authoriseCardDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          code: 'unknown_error',
          message: 'authoriseCardDetails failed',
        };
      });
  },
});

export const { resetTokenize } = tokenizeSlice.actions;
export default tokenizeSlice.reducer;
